'use client';
import React, { useState } from 'react';
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { DialogTitle } from "@/components/ui/dialog";
import { X } from 'lucide-react';
import MovingArrowsAnim from './moving-arrow';
import Image from 'next/image';
import icmLogo from '@/app/assets/images/icm-logo.png';
import biddLogo from '@/app/assets/images/bidd-logo.png';
import { ButtonSize, ButtonVariant, RegularButton } from '@/stories/button/button';
import { getBiddUrl, jumpToBiddUrl, setJumpConsent } from '@/app/api/service/bidd-redirect';
import { toast } from 'react-toastify';
import InfoBlue from '@/app/assets/icons/info-blue';

interface BiddRedirectPopupProps {
    popupRedirectToBidd: boolean;
    onClose: () => void;
    currentData: currentBondData;
}

export interface currentBondData {
    product?: string;
    productType?: string;
    redirectUrl?: string;
}

const BiddRedirectPopup:React.FC<BiddRedirectPopupProps> = ({
    popupRedirectToBidd, 
    onClose,
    currentData
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    const setConsent = async () => {
        try {
            const response = await setJumpConsent();
            
            if (!response.success) {
                throw new Error("Failed to set consent");
            } 
            if (response.success) {
                return;   
            }
            // Removed commented code for clarity
        } catch (error) {
            console.error("Error setting consent:", error);
            throw error; // Re-throwing is fine
        }
    };

    const getRedirectionUrl = async () => {
        try {
            // Added missing try/catch block
            const response = await getBiddUrl();
            if (!response || !response.success) {
                throw new Error("Unable to create redirection URL!");
            }
            const url = response.data?.url; // Added optional chaining
            if (!url) {
                throw new Error("Unable to create redirection URL!");
            }
            const token = await jumpToBiddUrl(url);
            if (!token) {
                throw new Error("Failed to get jump token");
            }

            // Check if currentData and environment variables exist
            if (
                !currentData?.productType ||
                !currentData?.product ||
                !process.env.ICM_BIDD_BONDS_POSTLOGIN_URL_HASH
            ) {
                throw new Error("Missing required data for URL generation");
            }

            return `${process.env.ICM_BIDD_URL}/${currentData.productType}//${currentData.product}?action=jump`;

            // if (currentData.productType === "bonds") {
            //     return `${process.env.ICM_BIDD_URL}/${currentData.productType}//${currentData.product}?action=jump`;
            // } else return `${process.env.ICM_BIDD_BONDS_POSTLOGIN_URL_HASH}biddeasy-asset/${currentData.productType}/${currentData.product}?action=jump`;
        } catch (error) {
            console.error("Error getting redirection URL:", error);
            throw error;
        }
    };

    const handleProceedClick = async () => {
        if (isLoading) return;
        setIsLoading(true);

        let newTab = null;

        try {
            // Open new tab immediately on click
            newTab = window.open("about:blank", "_blank");
            if (!newTab) {
                toast.error("Please allow popups for this site"); // Uncommented toast error
                throw new Error("Popup blocked");
            }

            // Show loading screen
            newTab.document.write(`<!DOCTYPE html>
                <html>
                <head>
                    <title>Loading...</title>
                    <style>
                    body {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        font-family: Arial, sans-serif;
                        background-color: #f5f5f5;
                    }
                    .loading-container {
                        text-align: center;
                    }
                    .loading-text {
                        margin-top: 20px;
                        color: #333;
                        font-size: 18px;
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 5px solid #f3f3f3;
                        border-top: 5px solid #3498db;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    </style>
                </head>
                <body>
                    <div class="loading-container">
                    <div class="spinner"></div>
                    <div class="loading-text">Please wait while we redirect you...</div>
                    </div>
                </body>
                </html>`
            );

            // Step 1: Set consent
            await setConsent();

            // Step 2: Get redirection URL
            const finalUrl = await getRedirectionUrl();

            // Step 3: Redirect the new tab
            if (newTab.closed) {
                throw new Error("Tab was closed during processing");
        }

            newTab.location.href = finalUrl;

            // Step 4: Close the modal
            onClose();
        } catch (error) {
            console.error("Redirection error:", error);
            if (newTab && !newTab.closed) {
                newTab.close();
            }
            toast.error("Something went wrong!"); // More specific error message
        } finally {
            setIsLoading(false);
        }
    };

  return (
    <Dialog.Root open={popupRedirectToBidd} data-testid="">
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-[#0F1016] bg-opacity-90" data-testid="" />
        <Dialog.Content 
            id='bidd-redirect-popup'
          className="fixed bottom-0 left-0 right-0 w-full max-h-max pt-s14 pb-s12 px-s12 bg-white shadow-lg rounded-t-[24px]
            md:bottom-auto md:h-[460px] md:w-[460px] md:rounded-[24px] md:top-1/2 md:left-1/2 md:max-w-md md:-translate-x-1/2 
            md:-translate-y-1/2 transition-transform duration-300 ease-in-out translate-y-0"
          data-testid=""
        >
          <VisuallyHidden.Root>
              <DialogTitle data-testid=""></DialogTitle>
          </VisuallyHidden.Root>
          <Dialog.Close asChild className='absolute' data-testid='bidd-redirect-popup-close'>
            <button
              className="md:top-[-50] top-[-3rem] md:right-[-10] right-0 p-2 text-neutral-900"
              onClick={onClose}
              data-testid='bidd-redirect-popup-close-btn'
            >
              <X size={28} className='text-white'/>
            </button>
          </Dialog.Close>
          <div className="flex flex-col gap-s11" data-testid="">
            <div className='flex flex-col gap-s12'>
                <div className='flex justify-between items-center gap-6 w-max mx-auto'>
                    <Image 
                        src={icmLogo}
                        width={35}
                        height={35}
                        alt='icm-logo'
                    />
                    <MovingArrowsAnim />
                    <Image 
                        src={biddLogo}
                        width={35}
                        height={35}
                        alt='bidd-logo'
                    />
                </div>
                <div className='flex flex-col gap-s9'>
                    <p className='text-center text-3xl font-semibold leading-[31.2px] tracking-[-0.24px]'>Connecting to Bidd</p>
                    <div className='w-full flex justify-between items-center gap-[10px]'>
                        <InfoBlue />
                        <p className='text-sm font-semibold leading-[22.4px] tracking-[0.14px] text-info-500'>
                            Kindly ensure you use the same mobile number on BIDD platform
                        </p>
                    </div>
                    <p className='text-center text-sm font-regular leading-[22.4px] tracking-[0.14px]'>
                        Transactions in this bond are processed via Bidd, a SEBI registered Online Bond Platform Provider. 
                        InCred Money is a partner to Bidd. By clicking on proceed you agree to the 
                        <span className='underline cursor-pointer' onClick={() => window.open(`${process.env.ICM_URL}//terms-of-use/`)}>Terms and Conditions</span>
                    </p>
                </div>
            </div>
            <div>
                <RegularButton
                    id='bidd-redirect-popup-proceed'
                    noIcon
                    showRightIcon={false}
                    className="!w-full max-w-full text-lg font-medium leading-[29.52px] tracking-[-0.18px]"
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    label={isLoading ? 'Processing...' : 'Proceed'}
                    onClick={handleProceedClick}
                    disabled={isLoading}
                />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default BiddRedirectPopup